import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Typography } from '@mui/material';

import { Header } from '../CommonComponents';
import { Container, Paper } from '../StyledComponents';

import { useDispatch, useSelector } from '../../redux/hooks';
import { fetchAllWatchlists, selectAllWatchlists } from '../../redux/actions/watchlists';
import { fetchAllOpenJobs, fetchAllOpenCapacities } from '../../redux/actions/market';
import { WatchlistForListType } from '../../types';

import {
    formatNotificationCount, getNotificationMode, getWatchlistTags, hasRoute
} from './formatWatchlist';
import CollapsibleSection from './parts/CollapsibleSection';
import ListRow from './parts/ListRow';
import NotificationModeControl from './parts/NotificationModeControl';
import TagChip from './parts/TagChip';
import useWatchlistNotificationMode from './parts/useWatchlistNotificationMode';

type SectionType = {
    title: string,
    items: WatchlistForListType[]
};

function getSections(watchlists: WatchlistForListType[]): SectionType[] {
    const routes = watchlists.filter((w) => w.source !== 'group' && hasRoute(w));
    const groups = watchlists.filter((w) => w.source === 'group');
    const own = watchlists.filter((w) => w.source !== 'group' && !hasRoute(w));

    return [
        { title: 'Våra rutter', items: routes },
        { title: 'Grupper', items: groups },
        { title: 'Egna', items: own }
    ].filter((section) => section.items.length > 0);
}

function formatSecondary(w: WatchlistForListType) {
    const parts = [formatNotificationCount(w.notificationCount30d || 0)];
    const kinds = [w.isJob && 'Uppdrag', w.isCapacity && 'Kapacitet'].filter(Boolean).join(' & ');
    if (kinds) {
        parts.push(kinds);
    }
    if (w.takesOffer) {
        parts.push('Intresseanmälningar');
    }
    return parts.join(' · ');
}

function WatchlistRow({ watchlist }: { watchlist: WatchlistForListType }) {
    const navigate = useNavigate();
    const { select, isSaving } = useWatchlistNotificationMode(watchlist);

    return (
        <ListRow
            title={watchlist.name || 'Namnlös bevakning'}
            dimmed={!watchlist.isActive}
            tags={getWatchlistTags(watchlist).map((tag) => (
                <TagChip key={tag} label={tag} outlined={tag === 'Pausad'} />
            ))}
            secondary={formatSecondary(watchlist)}
            onClick={() => navigate(`/watchlists/${watchlist.id}`)}
            action={(
                <NotificationModeControl
                    value={getNotificationMode(watchlist)}
                    onSelect={select}
                    disabled={isSaving}
                    label={`Notiser för ${watchlist.name || 'bevakningen'}`}
                />
            )}
        />
    );
}

export default function Watchlists() {
    const dispatch = useDispatch();
    const watchlists = useSelector(selectAllWatchlists());
    const sections = React.useMemo(() => getSections(watchlists), [watchlists]);

    const activeCount = watchlists.filter((w) => w.isActive).length;
    const summary = watchlists.length
        ? `${watchlists.length} ${watchlists.length === 1 ? 'bevakning' : 'bevakningar'} · ${activeCount} ${activeCount === 1 ? 'aktiv' : 'aktiva'}`
        : undefined;

    React.useEffect(() => {
        dispatch(fetchAllWatchlists());
        dispatch(fetchAllOpenJobs(true)); // Only fetch if not recently fetched
        dispatch(fetchAllOpenCapacities(true)); // Only fetch if not recently fetched
    }, [dispatch]);

    return (
        <Container>
            <Header title="Bevakningar" subtitle={summary} addUrl="/watchlists/create" />

            {sections.length === 0 && (
                <Paper>
                    <Typography>Du har inga bevakningar.</Typography>
                </Paper>
            )}

            {sections.map((section) => (
                <Paper key={section.title} padding={0} sx={{ mb: 2, overflow: 'hidden' }}>
                    <CollapsibleSection
                        title={section.title}
                        count={section.items.length}
                        storageKey={`watchlists.section.${section.title}`}
                    >
                        {section.items.map((watchlist) => (
                            <WatchlistRow key={watchlist.id} watchlist={watchlist} />
                        ))}
                    </CollapsibleSection>
                </Paper>
            ))}
        </Container>
    );
}
